var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Pane } from 'components/Pane';
import { Fragment, React, useEffect, useState } from 'libs/react';
import { useApolloClient } from '@apollo/client';
import { useAlert } from 'components/alert/AlertStack';
import { SendTaskListApprovalEmailMutation } from 'graphql/TaskList/mutations/SendTaskListApprovalEmailMutation';
import { FileButton } from 'components/FileButton';
import { Text } from 'components/Text';
import { GenerateOrderMutation } from 'graphql/Order/mutations/GenerateOrderMutation';
import { Panel } from 'components/Panel';
import { Table } from 'components/table/Table';
import { Button } from 'components/Button';
import { EmptyData } from 'components/EmptyData';
import { Icon } from 'components/Icon';
import { DateTime } from 'components/DateTime';
import { tracksQuantity } from 'helpers/optionConfiguration';
import { Money } from 'components/Money';
import { calculatePriceCurrency } from 'helpers/Pricing';
import './DealOrderPane.scss';
/**
 * @component DealOrderPane
 * @since 1.0.0
 */
export function DealOrderPane(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { deal, order, commandOnly } = Object.assign({}, props);
    const alert = useAlert();
    const apollo = useApolloClient();
    // The function sheet of the deal is shown under the order it came from
    const taskList = (deal === null || deal === void 0 ? void 0 : deal.taskList) && ((_a = deal.taskList.order) === null || _a === void 0 ? void 0 : _a.id) === (order === null || order === void 0 ? void 0 : order.id)
        ? deal.taskList
        : null;
    const [items, setItems] = useState((_b = taskList === null || taskList === void 0 ? void 0 : taskList.tasks) !== null && _b !== void 0 ? _b : []);
    useEffect(() => {
        var _a;
        setItems((_a = taskList === null || taskList === void 0 ? void 0 : taskList.tasks) !== null && _a !== void 0 ? _a : []);
    }, [taskList === null || taskList === void 0 ? void 0 : taskList.tasks]);
    // combine manufacturing items
    const manufacturingItems = [];
    items === null || items === void 0 ? void 0 : items.forEach((item) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const title = item.title || ((_a = item.saleItem) === null || _a === void 0 ? void 0 : _a.title) || '';
        const key = (_c = (_b = item.saleItem) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : `free-${title}`;
        const index = manufacturingItems.findIndex((manufacturingItem) => {
            return manufacturingItem.id === key;
        });
        if (index === -1) {
            manufacturingItems.push({
                id: key,
                quantity: item.quantity,
                sku: (_e = (_d = item.saleItem) === null || _d === void 0 ? void 0 : _d.sku) !== null && _e !== void 0 ? _e : '',
                title,
                infos: (_g = (_f = item.saleItem) === null || _f === void 0 ? void 0 : _f.infos) !== null && _g !== void 0 ? _g : '',
                trackQuantity: tracksQuantity(item.saleItem)
            });
        }
        else {
            manufacturingItems[index].quantity += item.quantity;
        }
    });
    // order by sku
    manufacturingItems.sort((a, b) => {
        if (a.sku < b.sku) {
            return -1;
        }
        if (a.sku > b.sku) {
            return 1;
        }
        return 0;
    });
    /**
     * @function onClickSendApprovalEmail
     * @since 1.0.0
     * @hidden
     */
    function onClickSendApprovalEmail() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!(taskList === null || taskList === void 0 ? void 0 : taskList.id))
                    return;
                yield apollo.mutate({
                    mutation: SendTaskListApprovalEmailMutation,
                    variables: {
                        input: {
                            id: taskList.id
                        }
                    }
                });
                alert.present({
                    title: React.createElement("p", null, t('Approval email has been sent.'))
                });
            }
            catch (error) {
                alert.present({ error });
            }
        });
    }
    const schema = {
        c0: {
            column: 'sku',
            header: t('SKU'),
            footer: t('SKU'),
            render: (value) => {
                return (React.createElement(Fragment, null,
                    React.createElement(Text, { value: value.sku })));
            }
        },
        c1: {
            column: 'title',
            header: t('Title'),
            footer: t('Title'),
            render: (value) => {
                return (React.createElement(Fragment, null,
                    React.createElement(Text, { value: value.title })));
            }
        },
        c2: {
            column: 'quantity',
            header: t('Quantity'),
            footer: t('Quantity'),
            render: (value) => {
                return (React.createElement(Fragment, null, value.trackQuantity !== false ? (React.createElement(Text, { value: value.quantity })) : null));
            }
        }
    };
    const doneCount = items.filter((item) => item.status === 'DONE').length;
    const approvedApprobation = (_c = taskList === null || taskList === void 0 ? void 0 : taskList.approbations) === null || _c === void 0 ? void 0 : _c.find((a) => a.state === 'ACCEPTED');
    const saleItemsPanel = ((_e = (_d = order === null || order === void 0 ? void 0 : order.items) === null || _d === void 0 ? void 0 : _d.length) !== null && _e !== void 0 ? _e : 0) > 0 ? (React.createElement(Panel, { title: `${t('Order')} ${(_f = order === null || order === void 0 ? void 0 : order.number) !== null && _f !== void 0 ? _f : ''}`, className: "deal-order-sale-items", collapsible: false },
        React.createElement("div", { className: "deal-order-sale-items-list" }, ((_g = order === null || order === void 0 ? void 0 : order.items) !== null && _g !== void 0 ? _g : []).map((item) => {
            var _a, _b, _c, _d, _e, _f;
            return (React.createElement("div", { key: item.id, className: "deal-order-sale-item" },
                React.createElement(Text, { className: "deal-order-sale-item-title", value: item.type === 'ITEM'
                        ? ((_c = (_a = item.title) !== null && _a !== void 0 ? _a : (_b = item.item) === null || _b === void 0 ? void 0 : _b.title) !== null && _c !== void 0 ? _c : '')
                        : `+ ${(_f = (_d = item.title) !== null && _d !== void 0 ? _d : (_e = item.item) === null || _e === void 0 ? void 0 : _e.title) !== null && _f !== void 0 ? _f : ''}` }),
                React.createElement(Text, { className: "deal-order-sale-item-qty", value: String(item.quantity) }),
                React.createElement(Money, { className: "deal-order-sale-item-price", value: calculatePriceCurrency(Number(item.price) * Number(item.quantity), deal === null || deal === void 0 ? void 0 : deal.currency) })));
        })),
        React.createElement("div", { className: "deal-order-sale-items-total" },
            React.createElement(Text, { value: t('Total') }),
            React.createElement(Money, { value: calculatePriceCurrency((_h = order === null || order === void 0 ? void 0 : order.total) !== null && _h !== void 0 ? _h : 0, deal === null || deal === void 0 ? void 0 : deal.currency) }),
            React.createElement(Button, { style: "picto", picto: "file-pdf", size: "small", onPress: () => {
                    window.open(`/orders/confirmation/${deal === null || deal === void 0 ? void 0 : deal.id}?order=${order === null || order === void 0 ? void 0 : order.id}`, '_blank');
                } })))) : commandOnly ? (React.createElement(Panel, { collapsible: false },
        React.createElement(EmptyData, { picto: "fal exclamation-triangle", title: t('No data'), description: t('There is no data to display.') }))) : null;
    if (commandOnly) {
        return React.createElement(Pane, null, saleItemsPanel);
    }
    return (React.createElement(Pane, null,
        React.createElement("div", { className: "deal-order-pane-bar" },
            approvedApprobation ? (React.createElement("div", { className: "deal-order-pane-approval-mention" },
                React.createElement(Icon, { value: "fad circle-check" }),
                React.createElement("span", { className: "deal-order-pane-approval-mention-label" },
                    t('Approved by'),
                    ' ',
                    [approvedApprobation.firstName, approvedApprobation.lastName]
                        .filter(Boolean)
                        .join(' '),
                    approvedApprobation.date && (React.createElement(React.Fragment, null,
                        ' ',
                        t('on'),
                        ' ',
                        React.createElement(DateTime, { value: approvedApprobation.date, y: "numeric", m: "numeric", d: "numeric" })))))) : (React.createElement("div", null)),
            React.createElement("div", { className: "deal-order-pane-actions" },
                taskList && (React.createElement("div", { className: "deal-order-pane-button-container" },
                    React.createElement(Button, { style: "picto", picto: "envelope", size: "small", onPress: onClickSendApprovalEmail }),
                    React.createElement(Text, { className: "deal-order-pane-button-value", value: t('Send approval') }))),
                React.createElement("div", { className: "deal-order-pane-file-button-container" },
                    React.createElement(FileButton, { picto: "file-pdf", exportMutation: GenerateOrderMutation, input: { id: order === null || order === void 0 ? void 0 : order.id }, className: "button" }),
                    React.createElement(Text, { className: "deal-order-pane-file-button-value", value: t('Download PDF') })))),
        saleItemsPanel,
        items.length > 0 ? (React.createElement(Panel, { title: t('Function sheet'), collapsible: false, className: "deal-order-pane-function-sheet" },
            React.createElement("div", { className: "deal-order-pane-function-sheet-bar" },
                React.createElement(Text, { value: `${doneCount} / ${items.length} ${t('completed')}` }),
                React.createElement(Button, { style: "primary", size: "small", picto: "arrow-right", label: t('Open the function sheet'), route: {
                        name: 'taskAndAction.update',
                        data: { id: taskList === null || taskList === void 0 ? void 0 : taskList.id }
                    } })),
            React.createElement(Table, { className: "deal-order-pane-summary-table", ready: true, schema: schema, values: manufacturingItems, sortable: [], defaultSortOrder: "desc", primaryColumn: "c1", emptyMessagePicto: "fal exclamation", emptyMessageTitle: t('No data'), emptyMessageInfos: t('There is no data to display.'), hideTableHeading: true }))) : (React.createElement(Panel, { collapsible: false },
            React.createElement(EmptyData, { picto: "fal exclamation-triangle", title: t('No tasks'), description: t('This order has no function sheet yet.') })))));
}
